import { Check, Wrench, Truck, Tablet, Clock, type LucideIcon } from "lucide-react";

// ============================================================================
// Status — THE single source of truth for dispatch/tech status presentation.
//
// Why this file exists: before 2026-05-28, "status" was rendered three
// different ways across the app — a solid circle+glyph on the Board, a
// struck-through pill on the Customer 360 Dispatches tab, and a pastel pill
// on the Dispatch detail page — each with its own hardcoded colors. Same
// fact, three costumes, and one color (pending = slate-400) failed WCAG
// contrast. This module defines each status ONCE; the <StatusBadge> component
// renders it in different shapes (icon / pill / dot) without ever changing
// the color, icon, or meaning.
//
// The pattern mirrors Linear / Stripe: a status has a canonical identity;
// only its on-screen size varies with available space.
//
// IMPORTANT: the badge displays the *raw* ESC status text as its label (e.g.
// "Done" vs "Complete" both render their own word). We only normalize for
// COLOR + ICON, never to rewrite the words a dispatcher already knows.
// ============================================================================

export type StatusKey =
  | "pending"
  | "dispatched"
  | "traveling"
  | "working"
  | "complete";

export interface StatusMeta {
  key: StatusKey;
  /** Lucide icon for the solid "icon" badge form. */
  Icon: LucideIcon;
  /** Solid fill (icon form) — white glyph sits on this. */
  solidBg: string;
  /** Small color dot used inside pill/dot forms (carries color even when tint is subtle — helps color-blind reading). */
  dot: string;
  /** Tinted background for the pill form. */
  pillBg: string;
  /** Text color for the pill/dot forms (AA-contrast against the tint / white). */
  pillText: string;
}

// Color choices are deliberately contrast-checked:
//   - pill = tint-50 background + 600/700 text  -> always passes WCAG AA 4.5:1
//   - solid = 500-weight fill + white glyph      -> glyph passes; dot >= 3:1
// This is what closes the old A11Y-01b "status pill contrast" finding.
const META: Record<StatusKey, StatusMeta> = {
  pending: {
    key: "pending",
    Icon: Clock,
    solidBg: "bg-slate-400",
    dot: "bg-slate-400",
    pillBg: "bg-slate-50",
    pillText: "text-slate-600",
  },
  dispatched: {
    key: "dispatched",
    Icon: Tablet,
    solidBg: "bg-sky-500",
    dot: "bg-sky-500",
    pillBg: "bg-sky-50",
    pillText: "text-sky-700",
  },
  traveling: {
    key: "traveling",
    Icon: Truck,
    solidBg: "bg-amber-500",
    dot: "bg-amber-500",
    pillBg: "bg-amber-50",
    pillText: "text-amber-700",
  },
  working: {
    key: "working",
    Icon: Wrench,
    solidBg: "bg-emerald-500",
    dot: "bg-emerald-500",
    pillBg: "bg-emerald-50",
    pillText: "text-emerald-700",
  },
  complete: {
    key: "complete",
    Icon: Check,
    solidBg: "bg-slate-500",
    dot: "bg-slate-400",
    pillBg: "bg-slate-100",
    pillText: "text-slate-600",
  },
};

/**
 * Normalize a raw ESC status string into a semantic status key.
 *
 * Uses substring matching (not exact equality) because ESC's status field
 * carries variants: "Traveling" / "On Way" / "Route", "Working" / "On Site",
 * "Dispatched" / "Assigned" / "Download", "Complete" / "Done". Ported from
 * the board's old statusIcon() so behavior is preserved, just centralized.
 *
 * Anything unrecognized (including null / empty) falls back to "pending".
 */
export function normalizeStatus(raw: string | null | undefined): StatusKey {
  const s = (raw ?? "").toLowerCase().trim();
  if (!s) return "pending";
  if (s.includes("complete") || s === "done" || s.includes("done"))
    return "complete";
  // 360-AUDIT #15: ESC's end-states that aren't the word "done" — thousands
  // of historical rows wore the not-done clock. Transfer = rolled to invoice,
  // Off Job = tech left the site: both are finished work for display.
  if (s.includes("transfer") || s.includes("off job"))
    return "complete";
  if (s.includes("need part") || s.includes("needs part"))
    return "working";
  if (s.includes("work") || s.includes("on site") || s.includes("onsite"))
    return "working";
  if (s.includes("trav") || s.includes("route") || s.includes("on way"))
    return "traveling";
  if (s.includes("disp") || s.includes("assigned") || s.includes("download"))
    return "dispatched";
  return "pending";
}

/** Look up the full presentation metadata for a raw status. */
export function statusMeta(raw: string | null | undefined): StatusMeta {
  return META[normalizeStatus(raw)];
}

/** The label the badge displays — the raw text, with a sensible default. */
export function statusLabel(raw: string | null | undefined): string {
  const t = (raw ?? "").trim();
  return t || "Pending";
}
